import React, { useMemo } from 'react';
import { CardContent } from '@mui/material';
import { Translate, useCommonTranslation } from 'c-translation';
import { LinkButton, TransparentCard } from 'c-components';
import { PostAuthRoutes } from 'c-routes';
import { FilterableEntityTable, generateColumn } from 'c-pagination';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import {
    CampaignEndDateConfig,
    CampaignScopes,
    CampaignStartDateConfig,
    CampaignStatusFilterConfig,
} from 'c-main/entityColumns';
import {
    CampaignListThumbnailCell,
    CampaignPlayoutsCell,
    CampaignStatusTableCell,
} from 'c-main/Components/Campaign/CampaignTableCells';
import { usePaginatedEntityData } from 'c-data';
import { generatePath } from 'react-router-dom';

import { useUserPermissions } from 'c-auth-module/Hooks';
import {
    costPerms,
    creativesPerms,
    displaysPerms,
    reportsPerms,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/constants';
import { campaignLandingRoute } from 'c-main/Pages/Campaign/CampaignOverviewPage/campaignLandingRoute';
import { useAuthenticatedUser } from 'c-hooks';

const columns = [
    generateColumn<Campaign>({
        headerTitle: '',
        field: 'thumbnail',
        entityName: 'Campaign',
        RenderCell: CampaignListThumbnailCell,
        permissions: [PermissionName.UniledportalFileRead],
    }),
    // generateColumn<Campaign>({
    //     headerTitle: 'type',
    //     field: 'type',
    //     entityName: 'Campaign',
    //     orderable: true,
    // }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.idCol',
        field: 'id',
        entityName: 'Campaign',
        orderable: true,
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.buyerCol',
        field: 'buyer.name',
        entityName: 'Campaign',
        orderable: true,
        orderByKey: 'buyer.name',
        filterKeyPath: 'buyer.id',
        permissions: [PermissionName.UniledportalBuyerRead],
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.brandCol',
        field: 'client.name',
        entityName: 'Campaign',
        orderable: true,
        orderByKey: 'client.name',
        filterKeyPath: 'client.id',
        permissions: [PermissionName.UniledportalClientRead],
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.nameCol',
        field: 'name',
        entityName: 'Campaign',
        orderable: true,
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.dateRangeCol',
        field: 'date',
        entityName: 'Campaign',
        dataType: 'dateRange',
        dateRangeProps: { startCol: 'date_start', endCol: 'date_end' },
        orderable: true,
        orderByKey: 'date_start',
        permissions: [PermissionName.UniledportalCampaignRead],
    }),

    // generateColumn<Campaign>({
    //     headerTitle: 'Modules.Main.Campaigns.List.table.statusCol',
    //     field: 'status',
    //     entityName: 'Campaign',
    // }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.displayCountCol',
        field: 'lineItemsCount',
        entityName: 'Campaign',
        textAlign: 'center',
        orderable: true,
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.referenceCol',
        field: 'reference',
        entityName: 'Campaign',
        orderable: true,
    }),

    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.reportingCol',
        field: 'playout_percentage',
        entityName: 'Campaign',
        orderable: true,
        permissions: [PermissionName.ReportingPcareportRead],
        RenderCell: CampaignPlayoutsCell,
    }),
    generateColumn<Campaign>({
        headerTitle: 'Modules.Main.Campaigns.List.table.statusCol',
        field: 'status',
        entityName: 'Campaign',
        RenderCell: CampaignStatusTableCell,
    }),
];

type Props = {
    paginationTag: string;
};
const includeFilters = ['filter.market.id', 'filter.client.id', 'filter.buyer.id'];
const customFilterConfig = [
    CampaignStatusFilterConfig,
    CampaignStartDateConfig,
    CampaignEndDateConfig,
];
const textSearchCols = ['name', 'reference', 'id', 'buyer.name'];
const includes = [
    'client',
    'buyer',
    'owners',
    'owners.owner',
    'creativeThumbnails',
    'lineItemsCount',
];

const CampaignListSearch: React.FC<Props> = ({ paginationTag }) => {
    const { hasAll } = useUserPermissions();
    const hasReportingPerms = hasAll(reportsPerms);
    const hasDisplayPerms = hasAll(displaysPerms);
    const hasCreativePerms = hasAll(creativesPerms);
    const hasCostPerms = hasAll(costPerms);
    const { paginatedRows } = usePaginatedEntityData(paginationTag, 'Campaign');
    const user = useAuthenticatedUser();
    return (
        <TransparentCard
            title={useCommonTranslation('Modules.Main.Campaigns.List.listTitle')}
            controls={[
                <IfHasAllPermissions
                    key="camp-create"
                    permissions={[PermissionName.UniledportalCampaignCreate]}
                >
                    <LinkButton size="small" to={PostAuthRoutes.Campaigns.NewCampaignPage}>
                        <Translate path="Modules.Main.Campaigns.List.createCampaign" />
                    </LinkButton>
                </IfHasAllPermissions>,
            ]}
        >
            <CardContent>
                <FilterableEntityTable
                    key={paginationTag}
                    tag={paginationTag}
                    columns={columns}
                    resetOnUnmount={false}
                    baseEntityName="Campaign"
                    showFilters
                    filters={useMemo(
                        () => ({ [CampaignScopes.scopeKey]: [CampaignScopes.notArchived] }),
                        [],
                    )}
                    routeTemplate={id => {
                        const row = paginatedRows[id] as Campaign | undefined;
                        return (
                            campaignLandingRoute(
                                { id, pca_available: row?.pca_available },
                                {
                                    reporting: hasReportingPerms,
                                    displays: hasDisplayPerms,
                                    creatives: hasCreativePerms,
                                    costs: hasCostPerms,
                                },
                                user?.user?.email,
                            ) ?? generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id })
                        );
                    }}
                    textSearchColumns={textSearchCols}
                    generateRowSx={(_row, _id) => ({ cursor: 'pointer' })}
                    includes={includes}
                    onlyIncludeFilterKeys={includeFilters}
                    customFilterConfig={customFilterConfig}
                    revertToIdSearchOnNumberOnlyInput={false}
                />
            </CardContent>
        </TransparentCard>
    );
};

export default CampaignListSearch;
